import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import { CheckCircle, Close, ContentCopy, OpenInNew } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import DisplayDate from "components/DisplayDate";
import { API_URL, maritalStatusOptions } from "const/constants";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { formatDisplayDate } from "utils/dateFormat";

const copyText = async (text, label = "Value") => {
  const value = String(text ?? "").trim();
  if (!value || value === "—") return;
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Could not copy to clipboard");
  }
};

const formatFieldValue = (value, type = "text") => {
  if (value == null || value === "") return "—";
  if (type === "date") return formatDisplayDate(value);
  if (type === "boolean") return value ? "Yes" : "No";
  if (type === "marital") {
    return maritalStatusOptions?.find((item) => item?.value === value)?.label || value;
  }
  if (type === "active") return value === "Y" ? "Active" : "Inactive";
  return String(value);
};

const DetailCell = ({ label, value, copyValue, className = "", isDate = false }) => {
  const display = value ?? "—";
  const rawCopy = copyValue ?? (display === "—" ? null : display);

  return (
    <div
      className={`group relative min-w-0 rounded-lg border border-app-border/70 bg-app-surface-muted/30 px-2.5 py-2 hover:border-primary/30 hover:bg-app-surface-muted transition-colors ${className}`}
    >
      <p className="text-[10px] uppercase tracking-wide text-app-text-muted font-semibold m-0 truncate">
        {label}
      </p>
      <div className="flex items-start gap-0.5 mt-0.5 min-h-[18px]">
        <p className="text-xs font-medium text-app-text m-0 flex-1 break-words leading-snug">
          {isDate ? <DisplayDate value={value} fallback="—" /> : display}
        </p>
        {rawCopy ? (
          <Tooltip title={`Copy ${label.toLowerCase()}`}>
            <IconButton
              size="small"
              onClick={() => copyText(rawCopy, label)}
              className="!p-0.5 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity shrink-0"
              aria-label={`Copy ${label}`}
            >
              <ContentCopy sx={{ fontSize: 13 }} className="text-app-text-secondary" />
            </IconButton>
          </Tooltip>
        ) : null}
      </div>
    </div>
  );
};

const SectionTitle = ({ children }) => (
  <p className="col-span-full text-[11px] font-bold uppercase tracking-wider text-primary m-0 pt-3 first:pt-0 pb-0.5 border-b border-app-border/60">
    {children}
  </p>
);

const buildClientSummary = (client) => {
  if (!client) return "";
  const fields = [
    ["Name", client.name],
    ["Email", client.email],
    ["Mobile", client.number],
    ["Date of birth", formatFieldValue(client.dob, "date")],
    ["Gender", client.gender],
    ["Marital status", formatFieldValue(client.maritalStatus, "marital")],
    ["USI", client.usiNumber],
    ["Passport", client.passportNumber],
    ["Passport issue", formatFieldValue(client.dateOfIssue, "date")],
    ["Passport expiry", formatFieldValue(client.passportExpiry, "date")],
    ["Visa type", client.visaStatus],
    ["Visa expiry", formatFieldValue(client.visaExpiry, "date")],
    ["Address", client.address],
    ["Onshore address", client.onshoreAddress],
    ["Home address", client.homeAddress],
    ["Emergency name", client.emergencyContactName],
    ["Emergency phone", client.emergencyContact],
    ["Emergency email", client.emergencyEmail],
    ["Emergency relation", client.emergencyRelation],
    ["Intake", client.intake],
    ["Course provider", client.currentCourseProvider],
    ["Course", client.courseName],
    ["Case officer", client.caseOfficer],
    ["Referral", client.referral || client.reference],
    ["Status", formatFieldValue(client.active, "active")],
    ["Converted", formatFieldValue(client.isConverted, "boolean")],
  ];
  return fields
    .filter(([, value]) => value && value !== "—")
    .map(([label, value]) => `${label}: ${value}`)
    .join("\n");
};

const ClientDetailsModal = ({ open, handleClose, clientId }) => {
  const navigate = useNavigate();

  const { data: clientData, isLoading } = useQuery(
    ["clientDetails", clientId],
    async () => {
      const response = await axios.get(`${API_URL}/clients/${clientId}`);
      return response.data?.data;
    },
    {
      enabled: !!clientId && open,
      onError: () => toast.error("Error fetching client details"),
    }
  );

  const summaryText = useMemo(() => buildClientSummary(clientData), [clientData]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { maxHeight: "92vh" } }}
    >
      <DialogTitle
        sx={{
          py: 1.5,
          px: 2,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "var(--app-surface-muted, #f8fafc)",
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-app-text-muted font-semibold m-0">
              Client profile
            </p>
            <h2 className="text-lg font-bold text-app-text m-0 truncate">
              {clientData?.name || "Client details"}
            </h2>
            {!isLoading && clientData ? (
              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                <Chip
                  size="small"
                  label={clientData?.active === "Y" ? "Active" : "Inactive"}
                  color={clientData?.active === "Y" ? "success" : "default"}
                  variant="outlined"
                  sx={{ height: 22, fontSize: 11 }}
                />
                <Chip
                  size="small"
                  icon={clientData?.isConverted ? <CheckCircle /> : undefined}
                  label={clientData?.isConverted ? "Converted" : "Not converted"}
                  color={clientData?.isConverted ? "success" : "warning"}
                  variant="outlined"
                  sx={{ height: 22, fontSize: 11 }}
                />
                {clientData?.caseOfficer ? (
                  <Chip
                    size="small"
                    label={`Officer: ${clientData.caseOfficer}`}
                    variant="outlined"
                    sx={{ height: 22, fontSize: 11 }}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {!isLoading && summaryText ? (
              <Tooltip title="Copy all details">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ContentCopy sx={{ fontSize: 16 }} />}
                  onClick={() => copyText(summaryText, "Client details")}
                  sx={{ fontSize: 12 }}
                >
                  Copy all
                </Button>
              </Tooltip>
            ) : null}
            <IconButton size="small" onClick={handleClose} aria-label="Close">
              <Close fontSize="small" />
            </IconButton>
          </div>
        </div>
      </DialogTitle>

      <DialogContent sx={{ p: 2, overflow: "auto" }}>
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <CircularProgress size={36} />
          </div>
        ) : !clientData ? (
          <p className="text-sm text-app-text-muted text-center py-8 m-0">No client data found.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            <SectionTitle>Personal</SectionTitle>
            <DetailCell label="Full name" value={clientData.name} />
            <DetailCell label="Email" value={clientData.email} />
            <DetailCell label="Mobile" value={clientData.number} />
            <DetailCell
              label="Date of birth"
              value={clientData.dob}
              copyValue={formatFieldValue(clientData.dob, "date")}
              isDate
            />
            <DetailCell label="Gender" value={clientData.gender} />
            <DetailCell
              label="Marital status"
              value={formatFieldValue(clientData.maritalStatus, "marital")}
            />
            <DetailCell label="USI number" value={clientData.usiNumber} />

            <SectionTitle>Passport &amp; visa</SectionTitle>
            <DetailCell label="Passport number" value={clientData.passportNumber} />
            <DetailCell
              label="Date of issue"
              value={clientData.dateOfIssue}
              copyValue={formatFieldValue(clientData.dateOfIssue, "date")}
              isDate
            />
            <DetailCell
              label="Passport expiry"
              value={clientData.passportExpiry}
              copyValue={formatFieldValue(clientData.passportExpiry, "date")}
              isDate
            />
            <DetailCell label="Visa type" value={clientData.visaStatus} />
            <DetailCell
              label="Visa expiry"
              value={clientData.visaExpiry}
              copyValue={formatFieldValue(clientData.visaExpiry, "date")}
              isDate
            />

            <SectionTitle>Addresses</SectionTitle>
            <DetailCell label="Full address" value={clientData.address} className="sm:col-span-2" />
            <DetailCell
              label="Onshore address"
              value={clientData.onshoreAddress}
              className="sm:col-span-2"
            />
            <DetailCell
              label="Home address"
              value={clientData.homeAddress}
              className="sm:col-span-2"
            />

            <SectionTitle>Emergency contact</SectionTitle>
            <DetailCell label="Contact name" value={clientData.emergencyContactName} />
            <DetailCell label="Contact number" value={clientData.emergencyContact} />
            <DetailCell label="Contact email" value={clientData.emergencyEmail} />
            <DetailCell label="Relation" value={clientData.emergencyRelation} />

            <SectionTitle>Course</SectionTitle>
            <DetailCell label="Intake" value={clientData.intake} />
            <DetailCell label="Course provider" value={clientData.currentCourseProvider} />
            <DetailCell
              label="Course name"
              value={clientData.courseName}
              className="sm:col-span-2"
            />

            <SectionTitle>Other</SectionTitle>
            <DetailCell label="Case officer" value={clientData.caseOfficer} />
            <DetailCell label="Referral" value={clientData.referral || clientData.reference} />
            <DetailCell
              label="Account status"
              value={formatFieldValue(clientData.active, "active")}
            />
            <DetailCell
              label="Converted"
              value={formatFieldValue(clientData.isConverted, "boolean")}
            />
          </div>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: "divider" }}>
        {clientId ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNew />}
            onClick={() => {
              handleClose();
              navigate(`/admin/client/view/${clientId}`);
            }}
          >
            Open full profile
          </Button>
        ) : null}
        <span className="flex-1" />
        <Button size="small" onClick={handleClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientDetailsModal;
